/* eslint-disable @typescript-eslint/no-explicit-any */
import { isPlatformBrowser } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DropdownModel } from '../models/ui.model';
import { StorageService } from './storage.service';
import { LanguageLocal } from '../enum/ui-utils.enum';

@Injectable({
    providedIn: 'root',
})
export class UtilitiesService {

    private readonly isBrowser: boolean;

    constructor(
        private translationService: TranslateService,
        private storageService: StorageService,
        @Inject(PLATFORM_ID) platformId: object,
    ) {
        this.isBrowser = isPlatformBrowser(platformId);
    }

    public trimFilterParams(body: object): HttpParams {
        let queryParams = new HttpParams();

        const filteredBody = Object.entries(body).reduce(
            (acc, [key, value]) => {
                if (key !== 'model') {
                    acc[key] = value;
                }
                return acc;
            },
            {} as Record<string, any>,
        );

        for (const [key, value] of Object.entries(filteredBody)) {
            if (
                value !== null &&
                value !== undefined &&
                value !== '' &&
                value !== 0 &&
                value !== 'null' &&
                !(Array.isArray(value) && value.length === 0)
            ) {
                queryParams = queryParams.append(key, value as string);
            }
        }

        return queryParams;
    }

    public generateYearDropdown(numberOfYears: number): DropdownModel[] {
        const currentYear = new Date().getFullYear();
        const years: DropdownModel[] = [];

        for (let i = 0; i < numberOfYears; i++) {
            years.push({
                value: currentYear - i,
                name: (currentYear - i).toString(),
            });
        }

        return years;
    }

    /**
     * Copy a string to the system clipboard.
     * Uses the async Clipboard API when available, falls back to a hidden
     * textarea + document.execCommand('copy') for older browsers / non-secure
     * contexts. Resolves to `true` only when the copy actually succeeded.
     * Safe to call on the server — returns `false` immediately.
     */
    public async copyToClipboard(text: string): Promise<boolean> {
        if (!this.isBrowser || !text) {
            return false;
        }

        if (navigator.clipboard?.writeText) {
            try {
                await navigator.clipboard.writeText(text);
                return true;
            } catch {
                // Permission denied / non-secure context — fall through.
            }
        }

        return this.fallbackCopyToClipboard(text);
    }

    private fallbackCopyToClipboard(text: string): boolean {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        try {
            return document.execCommand('copy');
        } catch {
            return false;
        } finally {
            document.body.removeChild(textarea);
        }
    }

    public getRemainingTimeString(duration:number, displayType: string = 'Full' ):String {
        let tempSpacing = (this.storageService.language == LanguageLocal.LanguageEN) ? " " : "";
        
        let tempDuration = duration; //duration is in minutes
        let tempStr = tempDuration  + tempSpacing + ((tempDuration > 1) ? this.translationService.instant('text.minutes' + displayType) : this.translationService.instant('text.minute' + displayType));

        if (tempDuration >= 60) {
            let tempHours = Math.floor(tempDuration / 60);
            let tempMins = Math.ceil(tempDuration % 60);

            let tempHourStr = tempHours  + tempSpacing +  ((tempHours > 1) ? this.translationService.instant('text.hours' + displayType) : this.translationService.instant('text.hour' + displayType));
            let tempMinStr = tempMins  + tempSpacing +  ((tempMins > 1) ? this.translationService.instant('text.minutes' +  displayType) : this.translationService.instant('text.minute' + displayType));

            tempStr = tempHourStr;

            if(tempMins){
              tempStr += tempSpacing + tempMinStr;
            }
        }

        return tempStr;
    }
}
